import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";

import AddCycleSidebar from "./AddCycleSidebar";
import CyclesCapacityView from "./CyclesCapacityView";
import CyclesListingView from "./CyclesListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";

import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Cycles",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CAPACITY: "Capacity View",
    VIEW_MODE_LIST: "List View",
    LABEL_NON_PRODUCTIVE: "Non-productive",
    LABEL_PRODUCTIVE: "Productive",
};

// Confirmation messages
const CYCLE_MESSAGES = {
    DELETE_CYCLES_BULK: (count) => `You are about to delete ${count} cycle${
        count !== 1 ? "s" : ""
    }.

This action cannot be undone. All cycle stages associated with these cycles will also be deleted.

Are you sure you wish to continue?`,
};

const Cycles = () => {
    const starekContext = useContext(StarekContext);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_CYCLES_LIST_FOR_PLANT = gql`
        query GetCycles($plantId: Int!) {
            cycles(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                cycle_stages {
                    id
                    name
                    is_productive
                    duration
                }
            }
        }
    `;

    const DELETE_ONE_CYCLE = gql`
        mutation deleteOneCycle($id: Int!) {
            deleteOneCycle(where: { id: $id }) {
                id
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_CYCLES_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [deleteOneCycle] = useMutation(DELETE_ONE_CYCLE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [viewMode, setViewMode] = useState("capacity"); // "capacity" or "list"
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [showAddCycleDrawer, setShowAddCycleDrawer] = useState(false);

    useEffect(() => {
        if (!data) return;
        setItems(data.cycles);
    }, [data]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.description?.toLowerCase().includes(q)
        );
    }, [items, search]);

    const handleAdd = () => {
        if (selectedRows.length >= 1) {
            setSelectedRows([]);
        };
        setShowAddCycleDrawer(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowAddCycleDrawer(true);
    };

    const handleDrawerClose = async () => {
        setShowAddCycleDrawer(false);
        if (selectedRows.length >= 1) {
            setSelectedRows([]);
        };
        await refetch();
    };

    const handleBulkDelete = async () => {
        if (selectedRows.length === 0 || isDeleting) return;
        setIsDeleting(true);
        setDeleteDone(0);

        try {
            for (const cycle of selectedRows) {
                try {
                    await deleteOneCycle({ variables: { id: cycle.id } });
                } catch (err) {
                    starekContext?.showError?.(err);
                } finally {
                    setDeleteDone((d) => d + 1);
                }
            }
            setSelectedRows([]);
            await refetch();
        } finally {
            setIsDeleting(false);
            setConfirmCfg({ open: false });
        }
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-recycle fa-10x text-success"></i>
            </div>
            <br />
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {items && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <EntityOperationBar
                        entity={{ singular: "Cycle", plural: "Cycles" }}
                        allowSearch={true}
                        allowAdd={true}
                        allowDelete={true}
                        allowEdit={true}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setSearch(text)}
                        onAdd={handleAdd}
                        onViewChange={(mode) => {
                            setViewMode(mode);
                            setSelectedRows([]);
                        }}
                        onEdit={handleEdit}
                        onDelete={handleBulkDelete}
                    >
                        <EntityOperationBar.View
                            mode="capacity"
                            icon="fa fa-chart-bar"
                            tooltip={UI_STRINGS.VIEW_MODE_CAPACITY}
                        >
                            {/* Legend for Capacity View */}
                            <div className="d-flex align-items-center mb-3">
                                <div className="me-4">
                                    <span className="bg-danger rounded-circle px-9px me-2"></span>
                                    {UI_STRINGS.LABEL_NON_PRODUCTIVE}
                                </div>
                                <div>
                                    <span className="bg-success rounded-circle px-9px me-2"></span>
                                    {UI_STRINGS.LABEL_PRODUCTIVE}
                                </div>
                            </div>
                            <CyclesCapacityView
                                items={filteredItems}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                filterText={search}
                            />
                        </EntityOperationBar.View>

                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip={UI_STRINGS.VIEW_MODE_LIST}
                        >
                            <CyclesListingView
                                items={filteredItems}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                filterText={search}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}

            <AddCycleSidebar
                show={showAddCycleDrawer}
                closeOffcanvas={handleDrawerClose}
                handleClose={handleDrawerClose}
                starekContext={starekContext}
                item={selectedRows[0] || ""}
                refetch={refetch}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Cycles };
